import { useToast } from '@teachino/shared-design-system';
import { $t } from '@teachino/shared-i18n';
import {
  type FragmentOf,
  graphql,
  readFragment,
} from '@teachino/shared-infrastructure';
import { safeLocalStorage } from '@teachino/shared-util';
import { useMutation, useQuery } from '@urql/vue';
import { computed } from 'vue';
import { featureFragment } from './feature.fragment';
import { personalLicenseFragment } from './personal-license.fragment';
import {
  type BillingCycle,
  requestCheckoutSessionDataMutation,
} from './request-checkout-session-data.mutation';
import { getPersonalTrialDayCounts } from './trial-license-day-count';

const upsellUrl = 'https://www.teachino.io/angebot-anfragen/';

const licenseFragment = graphql(`
  fragment License on License @_unmask {
    id
    holderType
    features {
      key
    }
    plan {
      id
      type
    }
  }
`);
export type License = FragmentOf<typeof licenseFragment>;

export function useLicense() {
  const toast = useToast();
  const licenseQuery = graphql(
    `
      query Licenses {
        licenses {
          ...License
          ...PersonalLicense
        }
      }
    `,
    [licenseFragment, personalLicenseFragment],
  );

  // Anonymous visitors (public student /c/ pages, shared-material previews)
  // must not fire this query: without a token the urql auth exchange treats
  // it as an auth error and hard-redirects to the teacher login page.
  const licenseQueryResult = useQuery({
    query: licenseQuery,
    pause: !safeLocalStorage.getItem('token'),
  });

  const requestCheckoutSessionData = useMutation(
    requestCheckoutSessionDataMutation,
  );
  const requestPaymentGatewayPortalDataMutation = useMutation(
    graphql(`
      mutation RequestPaymentGatewayPortalData(
        $paymentGatewayPortalInput: PaymentGatewayPortalInput!
      ) {
        requestPaymentGatewayPortalData(
          paymentGatewayPortalInput: $paymentGatewayPortalInput
        ) {
          url
          gateway
        }
      }
    `),
  );

  // All licenses the logged-in user can access (personal + school/tenant).
  const allUserLicenses = computed<License[]>(
    () =>
      licenseQueryResult.data.value?.licenses.map((license) =>
        readFragment(licenseFragment, license),
      ) ?? [],
  );

  const hasPaidPlan = computed<boolean>(() => {
    return allUserLicenses.value.some(
      (license) =>
        license.plan.type === 'PAID' || license.plan.type === 'FRIENDS',
    );
  });

  /**
   * Whether the Licenses query has produced an answer yet.
   *
   * Paywall helpers that consult the license list (trial expiry, grace window)
   * must not treat "still loading" as "no license" — that momentarily denies a
   * paying user and, for one-shot click handlers, pops an upgrade modal that
   * never should have appeared. Stays `false` for anonymous visitors, where the
   * query is paused on purpose.
   */
  const hasLoadedLicenses = computed<boolean>(
    () => licenseQueryResult.data.value !== undefined,
  );

  async function openProLicenseCheckout(args: {
    discountId?: string;
    billingCycle: BillingCycle;
  }) {
    const { discountId, billingCycle } = args;
    const { data, error } = await requestCheckoutSessionData.executeMutation({
      licensingSessionCheckoutInput: {
        planKey: 'teacher_pro',
        billingCycle,
        discountId,
      },
    });
    if (error) {
      toast.error($t('generic_error'));
      return;
    }
    const url = data?.requestCheckoutSessionData.url;
    if (url) {
      openUrlInNewTab(url);
    }
  }

  function openUrlInNewTab(url: string): void {
    const newTabOpened = window.open(url, '_blank');
    if (!newTabOpened) {
      window.location.href = url;
    }
  }

  async function startManageSubscription(licenseId: string) {
    const { data, error } =
      await requestPaymentGatewayPortalDataMutation.executeMutation({
        paymentGatewayPortalInput: {
          licenseId,
        },
      });
    if (error) {
      toast.error();
      return;
    }
    const url = data?.requestPaymentGatewayPortalData.url;
    if (url) {
      openUrlInNewTab(url);
    }
  }

  function openSchoolLicense() {
    openUrlInNewTab(upsellUrl);
  }

  function highlightsForFeatures(
    features: FragmentOf<typeof featureFragment>[],
  ): string[] {
    const unwrappedFeatures = features.map((feature) =>
      readFragment(featureFragment, feature),
    );
    // we don't want to show all jfeatures - here we control what we show

    const featureKeyDescriptionMap: Record<string, string> = {
      /* eslint-disable @typescript-eslint/naming-convention */
      integration_timetable: $t('upgrade.feature.integration_timetable'),
      calendar_personal_events: $t('upgrade.feature.daily_changes'),
      calendar_vacation: $t('upgrade.feature.daily_changes'),
      calendar_additional_periods: $t('upgrade.feature.daily_changes'),
      student_access: $t('upgrade.feature.student_access'),
      collaboration_substitutions: $t(
        'upgrade.feature.collaboration_substitutions',
      ),
      collaboration_course_templates: $t(
        'upgrade.feature.collaboration_course_templates',
      ),
      collaboration_teamteaching: $t(
        'upgrade.feature.collaboration_teamteaching',
      ),
      ai_basic: $t('upgrade.feature.ai_basic'),
      ai_advanced: $t('upgrade.feature.ai_advanced'),
      presentation_mode: $t('upgrade.feature.presentation'),
    };

    /* eslint-enable @typescript-eslint/naming-convention */

    const featureDescriptions = unwrappedFeatures.flatMap(
      (feature) => featureKeyDescriptionMap[feature.key] ?? [],
    );

    const uniqueFeatureDescriptions = Array.from(new Set(featureDescriptions));

    const hasUnlimitedFiles = unwrappedFeatures.some(
      (feature) => feature.key === 'files_unlimited',
    );

    return [
      ...uniqueFeatureDescriptions,
      ...(hasUnlimitedFiles
        ? [$t('upgrade.feature.files_unlimited')]
        : [$t('upgrade.feature.files_restricted')]),
    ];
  }

  // Subset of allUserLicenses: B2C licenses held by the user (holderType USER).
  const personalLicenses = computed<License[]>(() => {
    return allUserLicenses.value.filter(
      (license) => license.holderType === 'USER',
    );
  });

  // Raw licenses array from the GraphQL query (fragment masks still attached).
  const licenseQueryLicenses = computed(
    () => licenseQueryResult.data.value?.licenses ?? [],
  );

  // Same licenses as licenseQueryLicenses, parsed via PersonalLicense (dates, plan.key, …).
  // Not filtered — downstream paywall helpers pick which holder types / plans apply.
  const paywallLicenses = computed(() =>
    licenseQueryLicenses.value.map((license) =>
      readFragment(personalLicenseFragment, license),
    ),
  );

  const trialDayCounts = computed(() => {
    return getPersonalTrialDayCounts(paywallLicenses.value);
  });

  const isInTrial = computed(() => {
    return trialDayCounts.value !== null;
  });

  return {
    hasPaidPlan,
    hasLoadedLicenses,
    licenseQueryError: licenseQueryResult.error,
    openProLicenseCheckout,
    openSchoolLicense,
    startManageSubscription,
    highlightsForFeatures,
    personalLicenses,
    isInTrial,
    trialDayCounts,
    licenseQueryLicenses,
    paywallLicenses,
  };
}
